import { useNavigate } from 'react-router-dom';

import usePageRoutes from 'hooks/usePageRoutes';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import styles from './protoStyles.module.scss';
import type { FunctionComponent, Props } from './types';

const formatIsoDate = (input: string): string => {
    if (!input) {
        return '';
    }
    const parsed = new Date(input);

    return Number.isNaN(parsed.getTime()) ? input : parsed.toISOString().slice(0, 10);
};

const ChemicalsPage: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const { PAGE_SUBTITLE, PAGE_TITLE } = useTranslation();
    const { handleNewMix, mixes } = useData();
    const navigate = useNavigate();
    const { mixDetailsPage } = usePageRoutes();

    const handleMixClick = (mixId: string) => () => navigate(mixDetailsPage(mixId));

    return (
        <div className={`${styles.chemicalsPage} ${className}`} data-testid={testId}>
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderStack}>
                    <h1 className={styles.pageTitle}>{PAGE_TITLE}</h1>

                    <div className={styles.pageSubtitle}>{PAGE_SUBTITLE}</div>
                </div>
            </div>

            {mixes.length === 0 ? (
                <div className={styles.emptyState}>
                    No saved mixes yet. Click <strong>New Mix</strong> above to build your first
                    recipe.
                </div>
            ) : (
                <>
                    <div className={styles.mixRowHeader}>
                        <div>Name</div>

                        <div>Products</div>

                        <div>Crop</div>

                        <div>Last Used</div>

                        <div className={styles.mixRowHeaderSpacer} />
                    </div>

                    <div className={styles.mixesGrid}>
                        {mixes.map(mix => (
                            <button
                                className={styles.mixRow}
                                key={mix.id}
                                onClick={handleMixClick(mix.id)}
                                type='button'>
                                <div className={styles.mrNameCell}>
                                    <div className={styles.mrName}>{mix.name}</div>

                                    <div className={styles.mrMeta}>
                                        {mix.productNames.join(' · ')}
                                    </div>
                                </div>

                                <div className={styles.mrCount}>
                                    {mix.chemicalCount}{' '}

                                    {mix.chemicalCount === 1 ? 'product' : 'products'}
                                </div>

                                <div className={styles.mrCropCell}>
                                    <div className={styles.mrCrop}>{mix.crop ?? '—'}</div>

                                    {mix.hasDicamba && (
                                        <span
                                            className={`${styles.badge} ${styles.badgeSurfactant}`}>
                                            Dicamba
                                        </span>
                                    )}
                                </div>

                                <div className={styles.mrDate}>{formatIsoDate(mix.updatedAt)}</div>

                                <div className={styles.mrChevron}>
                                    <svg
                                        fill='none'
                                        height='18'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        viewBox='0 0 24 24'
                                        width='18'>
                                        <path d='m9 18 6-6-6-6' />
                                    </svg>
                                </div>
                            </button>
                        ))}
                    </div>
                </>
            )}

            <button
                aria-label='New mix'
                className={styles.newMixFab}
                onClick={handleNewMix}
                type='button'>
                +
            </button>
        </div>
    );
};

export default ChemicalsPage;
